import React from 'react';
import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';
import lodashTimes from 'lodash/fp/times';
import lodashCompact from 'lodash/fp/compact';
import lodashUniq from 'lodash/fp/uniq';
import lodashWithout from 'lodash/without';
import { countryOptionItems } from 'common/util/countryCodes';
import { stateOptionItems } from 'common/util/stateCodes';
import typeCheck from 'common/util/typeCheck';
import slugify from 'common/util/slugify';
import makeFullName from 'common/util/makeFullName';
import petOptionItems, { PET_OPTION_VALUE_DOG, dogBreedsOptionItems } from 'common/petOptionItems';

import vehicleTypeOptionItems from 'common/vehicleTypeOptionItems';

import { getProofOfIncomeQuestions } from 'common/householdApplication/incomeHelpers';

import { PREQUAL_MINIMUN_CREDIT_SCORE_OK } from 'common/householdConstants';

import { isListingTenantScreening } from 'common/listingHelpers';

import getRentalApplicationFormFields from 'common/householdApplication/rentalApplicationFormFields';

import { SR_FIELD_THEME_RENTAL_APPLICATION_VERTICAL_ADJUSTED } from 'client/components/SrFormikField';

import {
  SR_APPLICATION_ASSEMBLY_LINE_STEP_ID_MOVE_IN_DATE,
  SR_APPLICATION_ASSEMBLY_LINE_STEP_ID_INCOME_VERIFICATION,
} from 'common/applicationAssemblyLineConstants';

export const APPLICATION_FORM_TYPE_RENTER_PROFILE = 'APPLICATION_FORM_TYPE_RENTER_PROFILE';
export const APPLICATION_FORM_TYPE_GUARANTOR_APPLICATION =
  'APPLICATION_FORM_TYPE_GUARANTOR_APPLICATION';
export const APPLICATION_FORM_TYPE_RENTER_PREQUAL = 'APPLICATION_FORM_TYPE_RENTER_PREQUAL';
export const APPLICATION_FORM_TYPE_ADMIN_PREQUAL = 'APPLICATION_FORM_TYPE_ADMIN_PREQUAL';
export const APPLICATION_FORM_TYPE_SR_APPLICATION = 'APPLICATION_FORM_TYPE_SR_APPLICATION';
export const APPLICATION_FORM_TYPE_SR_APPLICATION_CREDIT_INFO =
  'APPLICATION_FORM_TYPE_SR_APPLICATION_CREDIT_INFO'; // eslint-disable-line max-len
export const APPLICATION_FORM_TYPE_SR_APPLICATION_RENTER_INFO =
  'APPLICATION_FORM_TYPE_SR_APPLICATION_RENTER_INFO'; // eslint-disable-line max-len
export const APPLICATION_FORM_TYPE_SR_APPLICATION_GUARANTOR_INFO =
  'APPLICATION_FORM_TYPE_SR_APPLICATION_GUARANTOR_INFO'; // eslint-disable-line max-len

const APPLICATION_FORM_TYPE_SR_APPLICATION_LIST = [
  APPLICATION_FORM_TYPE_SR_APPLICATION,
  APPLICATION_FORM_TYPE_SR_APPLICATION_CREDIT_INFO,
  APPLICATION_FORM_TYPE_SR_APPLICATION_RENTER_INFO,
  APPLICATION_FORM_TYPE_SR_APPLICATION_GUARANTOR_INFO,
];

export const renterPrequalFieldgroup = [
  'firstName',
  'lastName',
  'phone',
  'email',
  'prequalRoommateCount',
  'petCount',
  'pets.name',
  'pets.type',
  'pets.breed',
  'pets.weight',
  'pets.isEsa',
  'movePeriodStartDate',
  'movePeriodEndDate',
  'prequalHouseholdYearlyIncome',
  'creditScore',
  'renterPreviousCreditProblemsExplanation',
  'renterHasPreviouslyBeenEvicted',
  'renterHasPreviouslyBeenEvictedDate',
  'renterHasPreviouslyBeenEvictedExplanation',
  'renterPreviouslyFiledForBankruptcy',
  'renterPreviouslyFiledForBankruptcyDate',
  'renterPreviousBankruptcyExplanation',
  'renterPreviouslyConvitedOfCrime',
  'renterPreviouslyConvitedOfCrimeDate',
  'renterPreviousCrimeConvictionExplanation',
  'receiveHousingVouchers',
  'guarantorCount',
];

export const renterPrequalTenantScreeningListingFieldgroup = lodashWithout(
  renterPrequalFieldgroup,
  'firstName',
  'lastName',
  'phone',
  'email',
);

export const adminPrequalFieldgroup = [
  ...renterPrequalFieldgroup,
  'qualificationOverride',
  'notes',
];

// NOTE(@d-oliveros):
//  All the applications in a webmerge payload share these field IDs in the PDF documents
//  that are generated via WebMerge. If one renter updates one of these fields,
//  the applications for all renters in the households should be updated.
export const webmergeApplicationFieldgroup = [
  'firstName',
  'lastName',
  'email',
  'moveInDate',
  'roommateRelationships',
  'dateOfBirth',
  'ssn',
  'driverLicenseId',
  'driverLicenseStateIssued',
];

export const renterProfileFieldgroup = [
  'firstName',
  'lastName',
  'phone',
  'email',
  'roommateRelationships',
  'governmentIdPhoto',
  'driverLicenseId',
  'driverLicenseStateIssued',
  'dateOfBirth',
  'height',
  'weight',
  'hairColor',
  'eyeColor',
  'maritalStatus',
  'citizenship',
  'moveInDate',
  'leaseTerm',
  'currentJobCompany',
  'currentJobPosition',
  'currentJobSupervisorName',
  'currentJobSupervisorPhoneNumber',
  'currentJobStartDate',
  'currentJobEmployerEmail',
  'currentJobEmployerMailingAddress',
  'explanationOfIncome',
  'paystubPhotos',
  'previousJobCompany',
  'previousJobPosition',
  'previousJobMonthlyIncome',
  'previousJobSupervisorName',
  'previousJobSupervisorPhoneNumber',
  'previousJobEmployerEmail',
  'previousJobMailingAddress',
  'previousJobStartDate',
  'previousJobEndDate',
  'currentAddress',
  'currentLandlordName',
  'currentLandlordPhone',
  'currentLandlordEmail',
  'currentMonthlyRent',
  'currentRentalMoveInDate',
  'currentRentalMoveOutDate',
  'currentRentalReasonMovingOut',
  'previousRentalAddress',
  'previousLandlordName',
  'previousLandlordPhone',
  'previousLandlordEmail',
  'previousRentPrice',
  'previousRentalMoveInDate',
  'previousRentalMoveOutDate',
  'previousRentalReasonMovingOut',
  'emergencyContactName',
  'emergencyContactEmail',
  'emergencyContactPhone',
  'emergencyContactMailingAddress',
  'childrenCount',
  'children',
  'vehicleCount',
  'vehicles',
  'petCount',
  'pets',
  'hasWaterFilledFurniture',
  'renterInsurance',
  'inMilitary',
  'isMilitaryShortStay',
  'renterHasPreviouslyBeenEvicted',
  'renterHasPreviouslyBeenEvictedExplanation',
  'renterHasPreviouslyBeenEvictedDate',
  'renterHasPreviouslyBreachedLeaseContract',
  'renterPreviouslyAskedToMoveOut',
  'renterPreviouslyFiledForBankruptcy',
  'renterPreviousBankruptcyExplanation',
  'renterPreviouslyFiledForBankruptcyDate',
  'renterPrevioulsyLostPropertyForeclosure',
  'renterPreviouslyHadCreditProblems',
  'renterPreviousCreditProblemsExplanation',
  'renterPreviouslyConvitedOfCrime',
  'renterPreviousCrimeConvictionExplanation',
  'renterPreviouslyConvitedOfCrimeDate',
  'hasRegisteredSexOffenders',
  'hasPendingCriminalMatters',
  'pendingCriminalMatterExplanation',
  'ssn',
  'agreeTermsOfService',
];

export const guarantorApplicationFieldgroup = [
  'firstName',
  'lastName',
  'phone',
  'email',
  'roommateRelationships',
  'governmentIdPhoto',
  'driverLicenseId',
  'driverLicenseStateIssued',
  'dateOfBirth',
  'height',
  'weight',
  'hairColor',
  'eyeColor',
  'explanationOfIncome',
  'maritalStatus',
  'citizenship',
  'currentAddress',
  'currentJobCompany',
  'currentJobPosition',
  'currentJobSupervisorName',
  'currentJobSupervisorPhoneNumber',
  'currentJobStartDate',
  'currentJobEmployerEmail',
  'currentJobEmployerMailingAddress',
  'paystubPhotos',
  'ssn',
  'agreeTermsOfService',
];

export const srApplicationCreditInfoFieldgroup = [
  'firstName',
  'middleName',
  'lastName',
  'nameSuffix',
  'email',
  'phone',
  'dateOfBirth',
  'ssn',
  'confirmSsn',
  'noSsn',
  'currentAddressStreetName',
  'currentAddressApartmentUnit',
  'currentAddressCity',
  'currentAddressStateCode',
  'currentAddressZip',
];

export const srApplicationFinalFormFieldgroup = [
  'moveInDate',
  'leaseTerm',
  'rentalStatus',
  'rentalStatusExplanation',
  'proofOfHomeOwnershipUrl',
  'currentLandlordName',
  'currentLandlordEmail',
  'currentLandlordPhone',
  'currentMonthlyRent',
  'currentRentalMoveInDate',
  'currentRentalMoveOutDate',
  'currentRentalReasonMovingOut',
  'employmentStatus',
  'isEmployed',
  'statedYearlyIncome',
  'hasSourceOfIncome',
  'payRentExplanation',
  'currentJobCompany',
  'currentJobPosition',
  'currentJobEmployerMailingAddress',
  'currentJobSupervisorName',
  'currentJobSupervisorPhoneNumber',
  'currentJobEmployerEmail',
  'currentJobStartDate',
  'previousJobCompany',
  'previousJobPosition',
  'previousJobMailingAddress',
  'previousJobSupervisorName',
  'previousJobSupervisorPhoneNumber',
  'previousJobEmployerEmail',
  'previousJobStartDate',
  'previousJobEndDate',
  'incomeVerificationVOIE',
  'explanationOfIncome',
  'paystubPhotos',
  'bankStatementFileUrls',
  'offerLetterFileUrl',
  'additionalProofOfIncomePhotosCount',
  'additionalProofOfIncomePhotos',
  'idVerificationMethod',
  'governmentIdPhoto',
  'additionalDocuments',
  'driverLicenseId',
  'driverLicenseStateIssued',
  'noDriverLicense',
  'childrenCount',
  'children',
  'vehicleCount',
  'vehicles',
  'petCount',
  'pets',
  'hasWaterFilledFurniture',
  'renterInsurance',
  'inMilitary',
  'isMilitaryShortStay',
  'renterHasPreviouslyBeenEvicted',
  'renterHasPreviouslyBeenEvictedExplanation',
  'renterHasPreviouslyBeenEvictedDate',
  'renterHasPreviouslyBreachedLeaseContract',
  'renterPreviouslyAskedToMoveOut',
  'renterPreviouslyFiledForBankruptcy',
  'renterPreviousBankruptcyExplanation',
  'renterPreviouslyFiledForBankruptcyDate',
  'renterPrevioulsyLostPropertyForeclosure',
  'renterPreviouslyHadCreditProblems',
  'renterPreviousCreditProblemsExplanation',
  'renterPreviouslyConvitedOfCrime',
  'renterPreviousCrimeConvictionExplanation',
  'renterPreviouslyConvitedOfCrimeDate',
  'hasRegisteredSexOffenders',
  'hasPendingCriminalMatters',
  'pendingCriminalMatterExplanation',
  'additionalQuestions',
  'agreeTermsOfService',
];

export const srApplicationGuarantorFormFieldgroup = [
  'currentJobCompany',
  'currentJobPosition',
  'currentJobEmployerMailingAddress',
  'currentJobSupervisorName',
  'currentJobSupervisorPhoneNumber',
  'currentJobEmployerEmail',
  'currentJobStartDate',
  'previousJobCompany',
  'previousJobPosition',
  'previousJobMailingAddress',
  'previousJobSupervisorName',
  'previousJobSupervisorPhoneNumber',
  'previousJobEmployerEmail',
  'previousJobStartDate',
  'previousJobEndDate',
  'explanationOfIncome',
  'paystubPhotos',
  'bankStatementFileUrls',
  'offerLetterFileUrl',
  'additionalProofOfIncomePhotosCount',
  'additionalProofOfIncomePhotos',
  'governmentIdPhoto',
  'additionalDocuments',
  'driverLicenseId',
  'driverLicenseStateIssued',
  'noDriverLicense',
  'additionalQuestions',
  'agreeTermsOfService',
  'incomeVerificationVOIE',
];

export const srApplicationFormFieldgroupToAssemblyLineStepIdMapping = {
  [SR_APPLICATION_ASSEMBLY_LINE_STEP_ID_MOVE_IN_DATE]: ['moveInDate'],
  [SR_APPLICATION_ASSEMBLY_LINE_STEP_ID_INCOME_VERIFICATION]: [
    'currentJobStartDate',
    'incomeVerificationVOIE',
    'paystubPhotos',
    'bankStatementFileUrls',
    'offerLetterFileUrl',
    'additionalProofOfIncomePhotosCount',
    'additionalProofOfIncomePhotos',
  ],
};

export const requiredCreditAndBackgroundCheckFieldgroup = [
  'firstName',
  'lastName',
  'email',
  'phone',
  'dateOfBirth',
  'ssn',
  'currentAddressStreetName',
  'currentAddressCity',
  'currentAddressStateCode',
  'currentAddressZip',
];

export const virtualFormFieldIds = ['confirmSsn'];

export const editableFieldsFieldgroup = lodashUniq(
  [
    'hasStartedPrequal',
    'agreeRenterRequirements',
    'incomeVerificationVOIE',
    'incomeVerificationId',
    'verificationOfEmploymentTypeformResponse',
    'verificationOfEmploymentPdfUrl',
    'verificationOfEmploymentPdfCreatedDate',
    'verificationOfEmploymentAdditionalDocuments',
    'verificationOfRentalTypeformResponse',
    'verificationOfRentalPdfUrl',
    'verificationOfRentalPdfCreatedDate',
    'verificationOfRentalAdditionalDocuments',
    'verificationOfRentalStatus',
    'additionalQuestions',
    ...renterPrequalFieldgroup,
    ...adminPrequalFieldgroup,
    ...webmergeApplicationFieldgroup,
    ...renterProfileFieldgroup,
    ...guarantorApplicationFieldgroup,
    ...srApplicationCreditInfoFieldgroup,
    ...srApplicationFinalFormFieldgroup,
  ].map((id) => id.split('.')[0]),
);

export const applicationFormTypeToFieldgroupMapping = {
  [APPLICATION_FORM_TYPE_RENTER_PREQUAL]: renterPrequalFieldgroup,
  [APPLICATION_FORM_TYPE_GUARANTOR_APPLICATION]: guarantorApplicationFieldgroup,
  [APPLICATION_FORM_TYPE_ADMIN_PREQUAL]: adminPrequalFieldgroup,
  [APPLICATION_FORM_TYPE_RENTER_PROFILE]: renterProfileFieldgroup,
  [APPLICATION_FORM_TYPE_GUARANTOR_APPLICATION]: guarantorApplicationFieldgroup,
  [APPLICATION_FORM_TYPE_SR_APPLICATION_CREDIT_INFO]: srApplicationCreditInfoFieldgroup,
  [APPLICATION_FORM_TYPE_SR_APPLICATION_RENTER_INFO]: srApplicationFinalFormFieldgroup,
  [APPLICATION_FORM_TYPE_SR_APPLICATION_GUARANTOR_INFO]: srApplicationGuarantorFormFieldgroup,
  [APPLICATION_FORM_TYPE_SR_APPLICATION]: [
    ...srApplicationCreditInfoFieldgroup,
    ...srApplicationFinalFormFieldgroup,
    ...srApplicationGuarantorFormFieldgroup,
  ].filter((formFieldId) => !virtualFormFieldIds.includes(formFieldId)),
};

export function getAdditionalQuestionFields(additionalQuestions) {
  if (!Array.isArray(additionalQuestions)) {
    return [];
  }
  return additionalQuestions.map((question) => ({
    id: `additionalQuestions.${question.fieldId}`,
    title: question.title,
    label: question.label,
    type: question.type,
    validations: question.required ? ['required'] : [],
    properties: {
      className: SR_FIELD_THEME_RENTAL_APPLICATION_VERTICAL_ADJUSTED,
    },
  }));
}

export default function getApplicationFormFields(params) {
  typeCheck('params::NonEmptyObject', params);

  const {
    formValues,
    roommates,
    formType,
    hasBuyersAgent,
    setFieldValue,
    validateEmail,
    uploadAs,
    deleteAdditionalProofOfIncomeFile,
    renterProfile = {},
    listing = {},
    tenantScreeningCriteria = {},
    tenantAlertReport = {},
    onChangeEmploymentStatus,
    onPetCountChange,
    onVehicleCountChange,
    onChildrenCountChange,
    onShowInviteRoommateModal,
    isLeadRenter,
    incomeVerificationStatus,
    incomeVerificationProviderEnabled,
    additionalQuestions,
    moveInWarningText,
    requirePDFDocs,
    hasLandlordEmailChanged,
    hasLandlordPhoneChanged,
    hasSupervisorEmailChanged,
    hasSupervisorPhoneNumberChanged,
  } = params;

  typeCheck('formValues::Object', formValues);
  typeCheck('roommates::Maybe Array', roommates);
  typeCheck('formType::NonEmptyString', formType);
  typeCheck('hasBuyersAgent::Maybe Boolean', hasBuyersAgent);
  typeCheck('setFieldValue::Maybe Function', setFieldValue);
  typeCheck('validateEmail::Maybe Function', validateEmail);
  typeCheck('onChangeEmploymentStatus:: Maybe Function', onChangeEmploymentStatus);
  typeCheck('onPetCountChange:: Maybe Function', onPetCountChange);
  typeCheck('onVehicleCountChange:: Maybe Function', onVehicleCountChange);
  typeCheck('onChildrenCountChange:: Maybe Function', onChildrenCountChange);
  typeCheck('onShowInviteRoommateModal:: Maybe Function', onShowInviteRoommateModal);
  typeCheck('uploadAs::Maybe PositiveNumber', uploadAs);
  typeCheck('renterProfile::Object', renterProfile);
  typeCheck('listing::Maybe Object', listing);
  typeCheck('tenantScreeningCriteria::Object', tenantScreeningCriteria);
  typeCheck('isLeadRenter::Maybe Boolean', isLeadRenter);
  typeCheck('incomeVerificationStatus::Maybe String', incomeVerificationStatus);
  typeCheck('incomeVerificationProviderEnabled::Maybe Boolean', incomeVerificationProviderEnabled);
  typeCheck('additionalQuestionFields::Maybe Array', additionalQuestionFields);
  typeCheck('moveInWarningText::Maybe String', moveInWarningText);
  typeCheck('requirePDFDocs::Maybe Boolean', requirePDFDocs);

  const additionalQuestionFields = getAdditionalQuestionFields(additionalQuestions);

  let fieldgroup = applicationFormTypeToFieldgroupMapping[formType] || null;

  if (
    formType === APPLICATION_FORM_TYPE_RENTER_PREQUAL &&
    listing &&
    listing?.services &&
    isListingTenantScreening(listing)
  ) {
    fieldgroup = renterPrequalTenantScreeningListingFieldgroup;
  }

  typeCheck('fieldgroup::NonEmptyArray', fieldgroup);

  if (APPLICATION_FORM_TYPE_SR_APPLICATION_LIST.includes(formType)) {
    return getRentalApplicationFormFields({
      fieldgroup,
      formValues,
      setFieldValue,
      validateEmail,
      deleteAdditionalProofOfIncomeFile,
      renterProfile,
      listing,
      tenantScreeningCriteria,
      tenantAlertReport,
      hasBuyersAgent,
      uploadAs,
      isGuarantor: formType === APPLICATION_FORM_TYPE_SR_APPLICATION_GUARANTOR_INFO,
      onChangeEmploymentStatus,
      onPetCountChange,
      onVehicleCountChange,
      onChildrenCountChange,
      onShowInviteRoommateModal,
      isLeadRenter,
      incomeVerificationStatus,
      incomeVerificationProviderEnabled,
      disableSsnFieldsIfNoSsn: formType === APPLICATION_FORM_TYPE_SR_APPLICATION_CREDIT_INFO,
      additionalQuestionFields,
      moveInWarningText,
      requirePDFDocs,
      hasLandlordEmailChanged,
      hasLandlordPhoneChanged,
      hasSupervisorEmailChanged,
      hasSupervisorPhoneNumberChanged,
    });
  }

  const shouldShowDogPetQuestions = (petIndex) =>
    formValues.pets &&
    formValues.pets[petIndex] &&
    formValues.pets[petIndex].type === PET_OPTION_VALUE_DOG;
  const shouldShowEsaDocumentUpload = (petIndex) =>
    formValues.pets && formValues.pets[petIndex] && formValues.pets[petIndex].isEsa;

  const shouldShowGuarantorQuestion = () =>
    (formValues.prequalHouseholdYearlyIncome && formValues.prequalHouseholdYearlyIncome < 30600) ||
    (formValues.creditScore && formValues.creditScore < PREQUAL_MINIMUN_CREDIT_SCORE_OK);

  const isPrequalFormType = () =>
    formType === APPLICATION_FORM_TYPE_RENTER_PREQUAL ||
    formType === APPLICATION_FORM_TYPE_ADMIN_PREQUAL;

  const shouldShowCreditScoreExplanationQuestion = () =>
    isPrequalFormType()
      ? formValues.creditScore && formValues.creditScore < PREQUAL_MINIMUN_CREDIT_SCORE_OK
      : formValues.renterPreviouslyHadCreditProblems;

  let applicationFormFields = [
    {
      id: 'firstName',
      title: "What's your first name?",
      validations: ['required'],
      type: 'short_text',
    },
    {
      id: 'lastName',
      title: "What's your last name?",
      validations: ['required'],
      type: 'short_text',
    },
    {
      id: 'phone',
      title: "What's your phone?",
      validations: ['required', 'phone'],
      type: 'phone',
    },
    {
      id: 'email',
      title: "What's your email address?",
      validations: ['required', 'email'],
      properties: {
        placeholder: 'Write here...',
      },
      type: 'email',
    },
    {
      id: 'prequalRoommateCount',
      title: 'How many roommates are you moving with?',
      label: 'Not including yourself.',
      type: 'multiple_choice',
      validations: formType === APPLICATION_FORM_TYPE_ADMIN_PREQUAL ? [] : ['required'],
      properties: {
        choices: lodashTimes(
          (index) => ({
            label: index,
            value: index,
          }),
          4,
        ),
        multipleSelection: false,
      },
    },
    ...(roommates || []).map((roommate) => ({
      id: `roommateRelationships.user-${roommate.id}`,
      title: `How are you related to ${roommate.name}?`,
      label: 'Example: friend, boyfriend, wife, etc.',
      properties: {
        placeholder: 'Write here...',
      },
      validations: ['required'],
      type: 'short_text',
    })),
    {
      id: 'governmentIdPhoto',
      title: 'Government ID',
      label: "📷 Photo of Driver's License or Passport",
      validations: ['required'],
      type: 'file_single',
      properties: {
        fileAccept: '*',
        filePath: `/renter-application-uploads/${slugify(
          makeFullName(formValues.firstName, formValues.lastName),
        )}-id-{uuid}`,
        isPrivate: true,
        uploadAs: uploadAs,
      },
    },
    {
      id: 'driverLicenseId',
      title: "Driver's License ID number",
      label: '🔒 Your data is secured over SSL encryption',
      properties: {
        placeholder: 'Write here...',
        isPrivate: true,
        uploadAs: uploadAs,
      },
      validations: ['required'],
      type: 'short_text',
    },
    {
      id: 'driverLicenseStateIssued',
      title: "Driver's License: State Issued",
      properties: {
        placeholder: 'Texas',
      },
      validations: ['required'],
      type: 'short_text',
    },
    {
      id: 'dateOfBirth',
      title: 'Your date of birth',
      properties: {
        placeholder: 'MM/DD/YYYY',
      },
      validations: ['required', 'date'],
      type: 'date_input',
    },
    {
      id: 'height',
      title: "What's your height?",
      label: 'This should match your government ID.',
      properties: {
        placeholder: '5\' 6"',
      },
      validations: ['required'],
      type: 'short_text',
    },
    {
      id: 'weight',
      title: "What's your weight?",
      label: 'This should match your government ID.',
      properties: {
        placeholder: '150lbs',
      },
      validations: ['required'],
      type: 'short_text',
    },
    {
      id: 'hairColor',
      title: "What's your hair color?",
      label: 'This should match your government ID.',
      properties: {
        placeholder: 'Blonde',
      },
      validations: ['required'],
      type: 'short_text',
    },
    {
      id: 'eyeColor',
      title: "What's your eye color?",
      label: 'This should match your government ID.',
      properties: {
        placeholder: 'Blue',
      },
      validations: ['required'],
      type: 'short_text',
    },
    {
      id: 'prequalHouseholdYearlyIncome',
      title: (
        <span>
          What is your <span className="underline">yearly</span> household income?
        </span>
      ),
      label: (
        <span>
          {'How much do you and your roommates make combined in a '}
          <span className="underline">year</span>, before taxes?
        </span>
      ),
      properties: {
        placeholder: '$50,000',
      },
      validations: formType === APPLICATION_FORM_TYPE_ADMIN_PREQUAL ? [] : ['required'],
      type: 'currency',
    },
    {
      id: 'creditScore',
      title: 'What is your credit score?',
      label: "Don't know your credit? It's okay. Give us an estimate between 300 and 850!",
      validations:
        formType === APPLICATION_FORM_TYPE_ADMIN_PREQUAL ? [] : ['required', 'creditScore'],
      type: 'number',
      properties: {
        placeholder: '750',
      },
    },
    {
      id: 'explanationOfIncome',
      title: 'Additional explanation of income (optional)',
      label:
        'Example: "I\'m a server, so I make mostly tips. That income is not reflected in my pay stubs. However, I average $40k a year."',
      properties: {
        placeholder: 'Write here...',
      },
      type: 'long_text',
    },
    {
      id: 'moveInDate',
      title: 'Desired move-in date?',
      label: '📦📦📅',
      properties: {
        placeholder: 'Choose date...',
      },
      validations: isLeadRenter ? ['required', 'date'] : [],
      type: 'date_input',
    },
    {
      id: 'maritalStatus',
      title: 'Marital Status',
      validations: ['required'],
      type: 'multiple_choice',
      properties: {
        multipleSelection: false,
        choices: [
          {
            label: 'Married',
            value: 'married',
          },
          {
            label: 'Single',
            value: 'single',
          },
        ],
      },
    },
    {
      id: 'leaseTerm',
      title: 'How many months do you want to rent for?',
      validations: isLeadRenter ? ['required'] : [],
      properties: {
        placeholder: '12',
      },
      type: 'number',
    },
    {
      id: 'citizenship',
      title: 'Citizenship (country)',
      label: '🇺🇸🇲🇽🇬🇧',
      validations: ['required'],
      type: 'select',
      properties: {
        choices: countryOptionItems,
      },
    },
    {
      id: 'emergencyContactName',
      title: 'Emergency Contact: Name',
      label: 'Someone not living with you.',
      properties: {
        placeholder: 'John Smith',
      },
      validations: ['required'],
      type: 'short_text',
    },
    {
      id: 'emergencyContactEmail',
      title: 'Emergency Contact: Email',
      properties: {
        placeholder: 'jsmith@gmail.com',
      },
      validations: ['required', 'email'],
      type: 'email',
    },
    {
      id: 'emergencyContactPhone',
      title: 'Emergency Contact: Phone',
      properties: {
        placeholder: '5121234567',
      },
      validations: ['required', 'phone'],
      type: 'phone',
    },
    {
      id: 'emergencyContactMailingAddress',
      title: 'Emergency Contact: Mailing Address',
      properties: {
        placeholder: '123 Fake Street (unit 110), Austin, TX 78705',
      },
      validations: ['required'],
      type: 'short_text',
    },
    {
      id: 'currentAddress',
      title: 'Current Address 🏠',
      label: 'Where are you living? (street, city, state, zip)',
      properties: {
        placeholder: '123 Fake Street (unit 110), Austin, TX 78705',
      },
      validations: ['required'],
      type: 'short_text',
    },
    {
      id: 'currentLandlordName',
      title: 'Current Landlord: Name',
      properties: {
        placeholder: 'Jane Smith',
      },
      validations: ['required'],
      type: 'short_text',
    },
    {
      id: 'currentLandlordPhone',
      title: 'Current Landlord: Phone Number',
      properties: {
        placeholder: '5121234567',
      },
      validations: ['required', 'phone'],
      type: 'phone',
    },
    {
      id: 'currentLandlordEmail',
      title: 'Current Landlord: Email',
      properties: {
        placeholder: 'jsmith@gmail.com',
      },
      validations: ['required', 'email'],
      type: 'email',
    },
    {
      id: 'currentMonthlyRent',
      title: 'Current Rent',
      label: '💸 How much do you pay for rent now?',
      properties: {
        placeholder: '800',
      },
      validations: ['required'],
      type: 'number',
    },
    {
      id: 'currentRentalMoveInDate',
      title: 'Current Rental: Date moved in',
      properties: {
        placeholder: 'MM/DD/YYYY',
      },
      validations: ['required', 'date'],
      type: 'date_input',
    },
    {
      id: 'currentRentalMoveOutDate',
      title: 'Current Rental: Date moving out',
      label: 'When are you planning on moving?',
      properties: {
        placeholder: 'MM/DD/YYYY',
      },
      validations: ['required', 'date'],
      type: 'date_input',
    },
    {
      id: 'currentRentalReasonMovingOut',
      title: 'Current Rental: Reason for moving out',
      properties: {
        placeholder: 'Write here...',
      },
      validations: ['required'],
      type: 'short_text',
    },
    {
      id: 'previousRentalAddress',
      title: 'Previous Rental: Address 🏠',
      label: 'Before your current rental, where did you live?',
      properties: {
        placeholder: '123 Fake Street (unit 110), Austin, TX 78705',
      },
      validations: ['required'],
      type: 'short_text',
    },
    {
      id: 'previousLandlordName',
      title: 'Previous Landlord: Name',
      properties: {
        placeholder: 'Jacob Smith',
      },
      validations: ['required'],
      type: 'short_text',
    },
    {
      id: 'previousLandlordPhone',
      title: 'Previous Landlord: Phone Number',
      properties: {
        placeholder: '5121234567',
      },
      validations: ['required', 'phone'],
      type: 'phone',
    },
    {
      id: 'previousLandlordEmail',
      title: 'Previous Landlord: Email',
      properties: {
        placeholder: 'jsmith@gmail.com',
      },
      validations: ['required', 'email'],
      type: 'email',
    },
    {
      id: 'previousRentPrice',
      title: 'Previous Rental: Rent Price',
      label: '💸 How much did you pay for rent at your previous rental?',
      properties: {
        placeholder: '900',
      },
      validations: ['required'],
      type: 'number',
    },
    {
      id: 'previousRentalMoveInDate',
      title: 'Previous Rental: Date moved in',
      properties: {
        placeholder: 'MM/DD/YYYY',
      },
      validations: ['required', 'date'],
      type: 'date_input',
    },
    {
      id: 'previousRentalMoveOutDate',
      title: 'Previous Rental: Date moved out',
      properties: {
        placeholder: 'MM/DD/YYYY',
      },
      validations: ['required', 'date'],
      type: 'date_input',
    },
    {
      id: 'previousRentalReasonMovingOut',
      title: 'Previous Rental: Reason for moving out',
      properties: {
        placeholder: 'Write here...',
      },
      validations: ['required'],
      type: 'short_text',
    },
    {
      id: 'currentJobCompany',
      title: 'Current Employer Company 🏢',
      properties: {
        placeholder: 'Write here...',
      },
      validations: ['required'],
      type: 'short_text',
    },
    {
      id: 'currentJobPosition',
      title: 'Current Job: Position',
      properties: {
        placeholder: 'Write here...',
      },
      validations: ['required'],
      type: 'short_text',
    },
    {
      id: 'currentJobSupervisorName',
      title: "Current Job: Supervisor's Name",
      properties: {
        placeholder: 'Mr. Supervisor',
      },
      validations: ['required'],
      type: 'short_text',
    },
    {
      id: 'currentJobSupervisorPhoneNumber',
      title: "Current Job: Supervisor's Phone Number",
      properties: {
        placeholder: '5121234567',
      },
      validations: ['required', 'phone'],
      type: 'phone',
    },
    {
      id: 'currentJobStartDate',
      title: 'Current Job: Start date',
      label: '📅 When did you start working at your current job?',
      properties: {
        placeholder: 'MM/DD/YYYY',
      },
      validations: ['required', 'date'],
      type: 'date_input',
    },
    {
      id: 'currentJobEmployerEmail',
      title: "Current Job: Employer's email",
      properties: {
        placeholder: 'jsmith@gmail.com',
      },
      validations: ['required', 'email'],
      type: 'email',
    },
    {
      id: 'currentJobEmployerMailingAddress',
      title: "Current Job: Employer's mailing address",
      properties: {
        placeholder: '123 Fake Street (unit 110), Austin, TX 78705',
      },
      validations: ['required'],
      type: 'short_text',
    },
    {
      id: 'previousJobCompany',
      title: 'Previous Employer: Company Name 🏢',
      properties: {
        placeholder: 'Write here...',
      },
      validations: ['required'],
      type: 'short_text',
    },
    {
      id: 'previousJobPosition',
      title: 'Previous Job: Position',
      properties: {
        placeholder: 'Write here...',
      },
      validations: ['required'],
      type: 'short_text',
    },
    {
      id: 'previousJobMonthlyIncome',
      title: 'Previous Job: Monthly Income $',
      properties: {
        placeholder: '1400',
      },
      validations: ['required'],
      type: 'short_text',
    },
    {
      id: 'previousJobSupervisorName',
      title: "Previous Job: Supervisor's Name",
      properties: {
        placeholder: 'Jane Smith',
      },
      validations: ['required'],
      type: 'short_text',
    },
    {
      id: 'previousJobSupervisorPhoneNumber',
      title: "Previous Job: Supervisor's Phone Number",
      properties: {
        placeholder: '5121234567',
      },
      validations: ['required', 'phone'],
      type: 'phone',
    },
    {
      id: 'previousJobEmployerEmail',
      title: "Previous Job: Employer's email",
      properties: {
        placeholder: 'jsmith@gmail.com',
      },
      validations: ['required', 'email'],
      type: 'email',
    },
    {
      id: 'previousJobMailingAddress',
      title: "Previous Job: Employer's mailing address",
      properties: {
        placeholder: '123 Fake Street (unit 110), Austin, TX 78705',
      },
      validations: ['required'],
      type: 'short_text',
    },
    {
      id: 'previousJobStartDate',
      title: 'Previous Job: Start date',
      label: '📅 When did you start working at your previous job?',
      properties: {
        placeholder: 'MM/DD/YYYY',
      },
      validations: ['required', 'date'],
      type: 'date_input',
    },
    {
      id: 'previousJobEndDate',
      title: 'Previous Job: End date',
      label: 'When did you leave your previous job?',
      properties: {
        placeholder: 'MM/DD/YYYY',
      },
      validations: ['required', 'date'],
      type: 'date_input',
    },
    {
      id: 'childrenCount',
      title: 'How many people under 18 are going to live here?',
      validations: ['required'],
      type: 'multiple_choice',
      properties: {
        multipleSelection: false,
        choices: lodashTimes(
          (index) => ({
            label: index,
            value: index,
          }),
          6,
        ),
      },
    },
  ];

  for (let index = 0; index < Math.min(formValues.childrenCount || 0, 30); index += 1) {
    applicationFormFields.push(
      {
        id: `children[${index}].name`,
        title: `Dependent ${index + 1}: Name`,
        validations: ['required'],
        properties: {
          placeholder: 'Billy Bob',
        },
        type: 'short_text',
      },
      {
        id: `children[${index}].age`,
        title: `Dependent ${index + 1}: Age`,
        properties: {
          pattern: '[0-9]*',
          placeholder: '12',
          onlyAcceptNumbers: true,
        },
        validations: ['required', 'positiveNumber'],
        type: 'short_text',
      },
      {
        id: `children[${index}].relationship`,
        title: `Dependent ${index + 1}: Relationship`,
        label: 'Example: Son, daughter, etc.',
        properties: {
          placeholder: 'Daughter',
        },
        validations: ['required'],
        type: 'short_text',
      },
    );
  }

  applicationFormFields.push({
    id: 'vehicleCount',
    title: 'How many vehicles do you have?',
    label: '🚘🚘🏍️',
    validations: ['required'],
    type: 'multiple_choice',
    properties: {
      multipleSelection: false,
      choices: lodashTimes(
        (index) => ({
          label: index,
          value: index,
        }),
        5,
      ),
    },
  });

  for (let index = 0; index < Math.min(formValues.vehicleCount || 0, 30); index += 1) {
    applicationFormFields.push(
      {
        id: `vehicles[${index}].type`,
        title: `Vehicle ${index + 1}: Type`,
        label: '🚘🏍️🏎️',
        properties: {
          multipleSelection: false,
          choices: vehicleTypeOptionItems,
        },
        validations: ['required'],
        type: 'multiple_choice',
      },
      {
        id: `vehicles[${index}].model`,
        title: `Vehicle ${index + 1}: Make & Model`,
        validations: ['required'],
        type: 'short_text',
      },
      {
        id: `vehicles[${index}].color`,
        title: `Vehicle ${index + 1}: Color`,
        validations: ['required'],
        type: 'short_text',
      },
      {
        id: `vehicles[${index}].year`,
        title: `Vehicle ${index + 1}: Year`,
        validations: ['required'],
        type: 'number',
      },
      {
        id: `vehicles[${index}].licensePlateNumber`,
        title: `Vehicle ${index + 1}: License Plate Number`,
        validations: ['required'],
        type: 'short_text',
      },
      {
        id: `vehicles[${index}].licenseState`,
        title: `Vehicle ${index + 1}: License State`,
        validations: ['required'],
        type: 'select',
        properties: {
          choices: stateOptionItems,
        },
      },
      {
        id: `vehicles[${index}].monthlyPayment`,
        title: `Vehicle ${index + 1}: Monthly Payment`,
        validations: ['required'],
        type: 'short_text',
      },
    );
  }
  applicationFormFields.push({
    id: 'petCount',
    title: 'How many pets do you have?',
    label: '🐶🐱',
    validations: formType === APPLICATION_FORM_TYPE_ADMIN_PREQUAL ? [] : ['required'],
    type: 'multiple_choice',
    properties: {
      multipleSelection: false,
      choices: lodashTimes(
        (index) => ({
          label: index,
          value: index,
        }),
        5,
      ),
    },
  });

  for (let index = 0; index < Math.min(formValues.petCount || 0, 30); index += 1) {
    const petName = ((formValues.pets || [])[index] || {}).name;
    applicationFormFields.push(
      ...lodashCompact([
        {
          id: `pets[${index}].name`,
          title: `Pet ${index + 1}: Name`,
          properties: {
            placeholder: 'Lucky',
          },
          validations: formType === APPLICATION_FORM_TYPE_ADMIN_PREQUAL ? [] : ['required'],
          type: 'short_text',
        },
        {
          id: `pets[${index}].type`,
          title: `Is ${petName ? '' : 'pet '}${petName || index + 1} a dog or cat or other?`,
          label: '🐶😸🐠🐍',
          properties: {
            choices: petOptionItems,
            multipleSelection: false,
          },
          validations: formType === APPLICATION_FORM_TYPE_ADMIN_PREQUAL ? [] : ['required'],
          type: 'multiple_choice',
        },
        shouldShowDogPetQuestions(index) && {
          id: `pets[${index}].breed`,
          title: `What breed is ${petName ? '' : 'pet '}${petName || index + 1}?`,
          properties: {
            enableResetOption: true,
            choices: dogBreedsOptionItems,
          },
          validations: formType === APPLICATION_FORM_TYPE_ADMIN_PREQUAL ? [] : ['required'],
          type: 'select',
        },
        shouldShowDogPetQuestions(index) && {
          id: `pets[${index}].weight`,
          title: `How much does ${petName ? '' : 'pet '}${petName || index + 1} weigh?`,
          label: "Don't know it? Just give us an estimate! Answer in lbs.",
          properties: {
            placeholder: '20',
          },
          validations: formType === APPLICATION_FORM_TYPE_ADMIN_PREQUAL ? [] : ['required'],
          type: 'short_text',
        },
        {
          id: `pets[${index}].color`,
          title: `What color is ${petName ? '' : 'pet '}${petName || index + 1}?`,
          properties: {
            placeholder: 'Black',
          },
          validations: ['required'],
          type: 'short_text',
        },
        {
          id: `pets[${index}].age`,
          title: `How old is ${petName ? '' : 'pet '}${petName || index + 1}?`,
          properties: {
            placeholder: '3 yrs',
          },
          validations: ['required'],
          type: 'short_text',
        },
        {
          id: `pets[${index}].gender`,
          title: `Is ${petName ? '' : 'pet '}${petName || index + 1} male or female?`,
          validations: ['required'],
          type: 'multiple_choice',
          properties: {
            multipleSelection: false,
            choices: [
              {
                label: 'Male',
                value: 'male',
              },
              {
                label: 'Female',
                value: 'female',
              },
            ],
          },
        },
        {
          id: `pets[${index}].neutered`,
          title: `Is ${petName ? '' : 'pet '}${petName || index + 1} spayed/neutered?`,
          validations: ['required'],
          type: 'yes_no',
        },
        {
          id: `pets[${index}].declawed`,
          title: `Is ${petName ? '' : 'pet '}${petName || index + 1} declawed?`,
          validations: ['required'],
          type: 'yes_no',
        },
        {
          id: `pets[${index}].rabiesShotsCurrent`,
          title: `Is ${petName ? '' : 'pet '}${petName || index + 1} caught up on rabies shots?`,
          validations: ['required'],
          type: 'yes_no',
        },
        {
          id: `pets[${index}].isEsa`,
          title: `Is ${petName ? '' : 'pet '}${
            petName || index + 1
          } an emotional support or service animal?`,
          validations: formType === APPLICATION_FORM_TYPE_ADMIN_PREQUAL ? [] : ['required'],
          type: 'yes_no',
        },
        shouldShowEsaDocumentUpload(index) && {
          id: `pets[${index}].esaCertificate`,
          title: `Upload the ESA/Service document you have for ${petName}`,
          label: 'Please make sure the document is legible.',
          validations: ['required'],
          type: 'file_single',
          properties: {
            fileAccept: '*',
            filePath:
              '/renter-application-uploads/' +
              `${slugify(makeFullName(formValues.firstName, formValues.lastName))}` +
              `-pets-photo-esa-cert-${index + 1}-{uuid}`,
            isPrivate: true,
            uploadAs: uploadAs,
          },
        },
        {
          id: `pets[${index}].photo`,
          title: `Upload a photo of ${petName ? '' : 'pet '}${petName || index + 1}!`,
          label: `📸 Please include a full body shot of ${petName}`,
          validations: ['required'],
          type: 'file_single',
          properties: {
            fileAccept: '*',
            filePath:
              '/renter-application-uploads/' +
              `${slugify(makeFullName(formValues.firstName, formValues.lastName))}` +
              `-pets-photo-${index + 1}-{uuid}`,
            isPrivate: true,
            uploadAs: uploadAs,
          },
        },
      ]),
    );
  }
  applicationFormFields.push(
    ...lodashCompact([
      {
        id: 'movePeriodStartDate',
        title: "When's the earliest you can move?",
        label: 'You should be willing to start paying rent at this date 💸',
        type: 'date_picker',
        validations:
          formType === APPLICATION_FORM_TYPE_ADMIN_PREQUAL
            ? ['noPastDates', 'date']
            : ['required', 'noPastDates', 'date'],
        properties: {
          placeholder: momentWithAustinTimezone().format('MM/DD/YYYY'),
        },
      },
      {
        id: 'movePeriodEndDate',
        title: "When's the last day you have to be moved out by?",
        type: 'date_picker',
        validations:
          formType === APPLICATION_FORM_TYPE_ADMIN_PREQUAL
            ? ['noPastDates', 'date']
            : ['required', 'noPastDates', 'date'],
        properties: {
          placeholder: momentWithAustinTimezone().add(14, 'days').format('MM/DD/YYYY'),
        },
      },
      shouldShowGuarantorQuestion() && {
        id: 'renterHasGuarantor',
        title: 'Do you have access to a guarantor?',
        label:
          'A guarantor is someone who can financially back you (parent, guardian, relative). ' +
          'They are not your roommates. They are required if your income is less than 3X the rent.',
        properties: {
          placeholder: 'Write here...',
        },
        validations: formType === APPLICATION_FORM_TYPE_ADMIN_PREQUAL ? [] : ['required'],
        type: 'yes_no',
      },
      {
        id: 'hasWaterFilledFurniture',
        title:
          'Will any waterbeds or water-filled furniture, including aquariums, be on the Property?',
        label: '🌊🛏️ Weird question, we know.',
        validations: ['required'],
        type: 'yes_no',
      },
      {
        id: 'renterInsurance',
        title: "Will you maintain renter's insurance?",
        validations: ['required'],
        type: 'yes_no',
      },
      {
        id: 'inMilitary',
        title: 'Are you or your spouse, even if separated, in military?',
        validations: ['required'],
        type: 'yes_no',
      },
      formValues.inMilitary && {
        id: 'isMilitaryShortStay',
        title:
          "Is the military person serving under orders limiting the military person's stay to one year or less?",
        validations: ['required'],
        type: 'yes_no',
      },
      {
        id: 'renterHasPreviouslyBeenEvicted',
        title: 'Have you ever been evicted or broken a lease?',
        validations: formType === APPLICATION_FORM_TYPE_ADMIN_PREQUAL ? [] : ['required'],
        type: 'yes_no',
      },
      formValues.renterHasPreviouslyBeenEvicted && {
        id: 'renterHasPreviouslyBeenEvictedDate',
        title: 'When was the last time you got evicted or broke a lease?',
        properties: {
          placeholder: 'MM/DD/YYYY',
        },
        validations: formType === APPLICATION_FORM_TYPE_ADMIN_PREQUAL ? [] : ['required'],
        type: 'date_input',
      },
      formValues.renterHasPreviouslyBeenEvicted && {
        id: 'renterHasPreviouslyBeenEvictedExplanation',
        title: 'Please explain what caused you to be evicted or break a lease:',
        label: 'Any further details you can provide will help us find the best home for you.',
        properties: {
          placeholder: 'Write here...',
        },
        validations: formType === APPLICATION_FORM_TYPE_ADMIN_PREQUAL ? [] : ['required'],
        type: 'long_text',
      },
      {
        id: 'renterHasPreviouslyBreachedLeaseContract',
        title: 'Have you ever breached a lease or rental agreement?',
        validations: ['required'],
        type: 'yes_no',
      },
      {
        id: 'renterPreviouslyAskedToMoveOut',
        title: 'Have you ever been asked by a landlord to move out?',
        validations: ['required'],
        type: 'yes_no',
      },
      {
        id: 'renterPreviouslyFiledForBankruptcy',
        title: 'Have you ever filed for bankruptcy?',
        validations: ['required'],
        type: 'yes_no',
      },
      formValues.renterPreviouslyFiledForBankruptcy && {
        id: 'renterPreviouslyFiledForBankruptcyDate',
        title: 'When was the last time you filed for bankruptcy?',
        properties: {
          placeholder: 'MM/DD/YYYY',
        },
        validations: ['required'],
        type: 'date_input',
      },
      formValues.renterPreviouslyFiledForBankruptcy && {
        id: 'renterPreviousBankruptcyExplanation',
        title: 'Please explain what caused the bankruptcy:',
        label:
          'The landlord will want a full explanation. Take as much room as you want to ' +
          'fill this out.\n\nWhen was the bankruptcy? What caused it? What can you tell ' +
          "the landlord, to show that you're not a risk or not as big of a risk as other " +
          'applicants? \n\nThis is your chance to explain it in a good light.',
        properties: {
          placeholder: 'Write here...',
        },
        validations: ['required'],
        type: 'long_text',
      },
      {
        id: 'renterPrevioulsyLostPropertyForeclosure',
        title: 'Have you ever lost property in a foreclosure?',
        validations: ['required'],
        type: 'yes_no',
      },
      {
        id: 'renterPreviouslyHadCreditProblems',
        title: 'Have you had any credit problems, slow-pays or delinquencies?',
        validations: ['required'],
        type: 'yes_no',
      },
      shouldShowCreditScoreExplanationQuestion() && {
        id: 'renterPreviousCreditProblemsExplanation',
        title: isPrequalFormType()
          ? 'Please explain anything that may have negatively affected your credit score:'
          : 'Please explain what caused the credit problems:',
        label: isPrequalFormType()
          ? 'Any further details you can provide will help us find the best home for you.'
          : 'The landlord will want a full explanation. Take as much room as you want to ' +
            'fill this out.\n\nWhen was your credit lowered? What caused it? What can you ' +
            "tell the landlord, to show that you're not a risk or not as big of a risk as " +
            'other applicants? \n\nThis is your chance to explain it in a good light.',
        properties: {
          placeholder: 'Write here...',
        },
        validations: isPrequalFormType() ? [] : ['required'],
        type: 'long_text',
      },
      {
        id: 'hasRegisteredSexOffenders',
        title: 'Is any occupant a registered sex offender?',
        validations: ['required'],
        type: 'yes_no',
      },
      {
        id: 'hasPendingCriminalMatters',
        title: 'Are there any criminal matters pending against any occupant?',
        validations: ['required'],
        type: 'yes_no',
      },
      {
        id: 'receiveHousingVouchers',
        title: 'Do you receive Section 8 / housing vouchers?',
        validations: ['required'],
        type: 'yes_no',
      },
      formValues.hasPendingCriminalMatters && {
        id: 'pendingCriminalMatterExplanation',
        title: 'Please explain the criminal matters:',
        label:
          'The landlord will want a full explanation. Take as much room as you want to fill ' +
          "this out.\n\nWhat caused it? What can you tell the landlord, to show that you're " +
          'not a risk or not as big of a risk as other applicants? \n\nThis is your ' +
          'chance to explain it in a good light.',
        properties: {
          placeholder: 'Write here...',
        },
        validations: ['required'],
        type: 'long_text',
      },
      ...getProofOfIncomeQuestions({
        currentJobCompany: formValues.currentJobCompany,
        firstName: formValues.firstName,
        lastName: formValues.lastName,
        uploadAs: uploadAs,
      }),
      {
        id: 'ssn',
        title: 'SSN/ITIN',
        label: '🔒 Your data is secured over SSL encryption',
        properties: {
          placeholder: 'XXX-XX-XXXX',
          dataDogMasked: true,
        },
        validations: ['required'],
        type: 'short_text',
      },
      {
        id: 'agreeTermsOfService',
        title: 'Terms of Service',
        label: (
          <div>
            <div style={{ textAlign: 'justify' }}>
              {hasBuyersAgent &&
                'By electronically signing the Authorization to Release Information hyperlinked below, ' +
                  'you consent Sunroom Rentals to run an income and rental history verification.'}
            </div>
            <br />
            <div>
              Authorization to Release Information:&nbsp;
              <a
                className="sr-link"
                target="_blank"
                rel="noopener noreferrer"
                href="http://bit.ly/authsunroom"
              >
                http://bit.ly/authsunroom
              </a>
            </div>
          </div>
        ),
        validations: ['legal'],
        type: 'checkbox',
      },
      {
        id: 'qualificationOverride',
        title: 'Qualification Override',
        type: 'select',
        properties: {
          enableResetOption: true,
          choices: [
            {
              label: 'Yes Qualified',
              value: true,
            },
            {
              label: 'NO Qualified',
              value: false,
            },
          ],
        },
      },
      {
        id: 'notes',
        title: 'notes?',
        type: 'long_text',
        properties: {
          placeholder: 'Renter wants to live in...',
        },
      },
      {
        id: 'guarantorCount',
        title: 'Do you need any Guarantors?',
        label:
          'They are not your roommates. ' +
          'A guarantor is someone who can financially back you (parent, guardian, relative). ' +
          'They are required if your income is less than 3X the rent.',
        validations: ['required'],
        type: 'multiple_choice',
        properties: {
          multipleSelection: false,
          choices: [
            {
              label: 'None',
              value: 0,
            },
            {
              label: '1',
              value: 1,
            },
            {
              label: '2',
              value: 2,
            },
          ],
        },
      },
      ...(additionalQuestionFields || []),
    ]),
  );

  if (fieldgroup) {
    // NOTE(@d-oliveros):
    //  Filters form field IDs out by nested ID string or by base ID string.
    //  For example: 'pets' and 'pets.weight' are both valid options and will
    //  include either the 'pets' array field with all the nested form fields,
    //  or the 'pets' array with only a subset of form fields.
    //  Indexes are not allowed, like 'pets[0].weight'.
    applicationFormFields = lodashCompact(
      fieldgroup.reduce((memo, fieldId) => {
        memo.push(
          ...applicationFormFields.filter(({ id }) => {
            const idWithoutIndexes = id.replace(/\[[^\]]*]/g, '');
            const baseId = idWithoutIndexes.split('.')[0];
            return fieldId === idWithoutIndexes || fieldId === baseId;
          }),
        );
        return memo;
      }, []),
    );
  }

  return applicationFormFields;
}
